import type { IProductIDData } from '@surfside/ads-react';

import { GProduct } from '@/types';

export const VIEWABILITY_THRESHOLD = 0.5;
export const VIEWABILITY_DWELL_MS = 1000;

/** OpenRTB Native 1.2 event ViewableMRC50. Omitted from the bid unless requested. */
export const VIEWABLE_MRC50_EVENT = 2;
/** OpenRTB Native 1.2 image pixel. */
export const IMAGE_PIXEL_METHOD = 1;

const VIEWABLE_TRACKER_KEYS = ['viewable', 'viewableMRC50'] as const;

export interface EnrichedProduct extends GProduct {
  surfsideType?: 'sponsored' | 'recommended';
}

type PixelTracker = { type: 'pixel'; url: string };

export const getRecommenderType = (
  recommend: 'for-you' | 'top-products' | 'buy-again' | null | undefined,
): 'for-you' | 'top-products' | 'past-purchases' => {
  if (recommend === 'for-you') return 'for-you';
  if (recommend === 'buy-again') return 'past-purchases';
  return 'top-products';
};

export const getSurfsideProductId = (
  product: IProductIDData,
): string | undefined => {
  const id = product.data?.id;
  return id ? String(id).trim() : undefined;
};

export const getSurfsideProductType = (
  product: IProductIDData,
): 'sponsored' | 'recommended' | undefined => product.type;

export const findSurfsideProductById = (
  products: IProductIDData[] | undefined,
  id?: string,
) => {
  if (!id) return undefined;
  return products?.find(item => getSurfsideProductId(item) === id);
};

export const getFirstSponsoredCard = (
  surfsideProducts: IProductIDData[] | undefined,
  enrichedProducts: EnrichedProduct[],
): IProductIDData | undefined => {
  const renderedSponsoredIds = new Set(
    enrichedProducts.flatMap(product => {
      if (product.surfsideType !== 'sponsored' || !product.id) return [];
      return [String(product.id).trim()];
    }),
  );

  if (!renderedSponsoredIds.size) return undefined;

  return (surfsideProducts ?? []).find(product => {
    const id = getSurfsideProductId(product);
    return Boolean(id && renderedSponsoredIds.has(id));
  });
};

const getPixelTrackers = (
  product: IProductIDData,
  keys: readonly string[],
): PixelTracker[] => {
  const namedTrackers = product.productCardResponse.trackers?.namedTrackers;
  if (!namedTrackers) return [];

  const seenUrls = new Set<string>();
  const pixels: PixelTracker[] = [];

  keys.forEach(key => {
    namedTrackers[key]?.forEach(tracker => {
      if (tracker.type !== 'pixel' || seenUrls.has(tracker.url)) return;
      seenUrls.add(tracker.url);
      pixels.push(tracker);
    });
  });

  return pixels;
};

export const fireViewableTrackers = (product: IProductIDData) => {
  getPixelTrackers(product, VIEWABLE_TRACKER_KEYS).forEach(tracker => {
    const url = tracker.url.replace(/^http:\/\//i, 'https://');
    void fetch(url, {
      keepalive: true,
      mode: 'no-cors',
      referrerPolicy: 'origin-when-cross-origin',
    });
  });
};

const fireUnseenPixel = (
  product: IProductIDData,
  seenUrls: Set<string>,
  trackerKey: 'win' | 'impression',
  track: (_product: IProductIDData) => void,
) => {
  const trackers = getPixelTrackers(product, [trackerKey]);
  if (!trackers.length) return;
  if (trackers.every(tracker => seenUrls.has(tracker.url))) return;

  track(product);
  trackers.forEach(tracker => seenUrls.add(tracker.url));
};

export const fireCarouselPixels = (
  product: IProductIDData,
  analytics: {
    trackWin: (_product: IProductIDData) => void;
    trackImpression: (_product: IProductIDData) => void;
  },
  seenWinUrls: Set<string>,
  seenImpressionUrls: Set<string>,
) => {
  fireUnseenPixel(product, seenWinUrls, 'win', analytics.trackWin);
  fireUnseenPixel(
    product,
    seenImpressionUrls,
    'impression',
    analytics.trackImpression,
  );
};
